import { Link } from "react-router-dom";
import { Plus, Sparkles } from "lucide-react";
import { useCart } from "@/context/CartContext";

const Tile = ({ role, p, badge }) => {
  const { addItem } = useCart();
  if (!p) return null;
  return (
    <div data-testid={`companion-${p.id}`} className="bg-charcoal border border-white/10 rounded-lg overflow-hidden flex flex-col hover:border-gold/30 transition-colors">
      <Link to={`/product/${p.handle}`} className="relative block aspect-[4/3] overflow-hidden bg-espresso">
        <img src={(p.images || [])[0]} alt={p.title} loading="lazy"
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
        {badge && (
          <span className="absolute top-2 left-2 bg-gold text-espresso text-[10px] font-semibold px-2 py-0.5 rounded uppercase tracking-wide">
            {badge}
          </span>
        )}
      </Link>
      <div className="p-3 flex flex-col flex-1">
        <p className="text-[10px] uppercase tracking-wider text-gold/80">{role}</p>
        <Link to={`/product/${p.handle}`} className="text-cream/90 text-sm leading-tight mt-0.5 line-clamp-2 hover:text-gold transition-colors">
          {p.title}
        </Link>
        <div className="mt-auto pt-3 flex items-center justify-end">
          <button data-testid={`companion-add-${p.id}`} onClick={() => addItem({ productId: p.id, quantity: 1 })}
            className="flex items-center gap-1 bg-gold/15 text-gold border border-gold/40 px-2.5 py-1 rounded text-xs hover:bg-gold/25 transition-colors">
            <Plus size={13} /> Add
          </button>
        </div>
      </div>
    </div>
  );
};

const Group = ({ title, children }) => {
  const tiles = (Array.isArray(children) ? children : [children]).filter(Boolean);
  if (!tiles.length) return null;
  return (
    <div className="mt-6">
      <h3 className="font-serif text-lg text-cream/90 mb-3">{title}</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">{tiles}</div>
    </div>
  );
};

export function ProductCompanions({ companions }) {
  if (!companions) return null;

  if (companions.type === "sauna") {
    const c = companions;
    const otherHeaters = (c.otherHeaters || []).filter((h) => h && h.id !== (c.bestHeater || {}).id);
    return (
      <div className="mt-16" data-testid="sauna-companions">
        <p className="overline flex items-center gap-2"><Sparkles size={13} /> Complete Your Setup</p>
        <h2 className="font-serif text-2xl sm:text-3xl text-cream mt-2">Build the full recovery experience</h2>
        {c.infrared ? (
          <p className="text-cream/55 text-sm mt-2">This infrared cabin includes built-in heating — no separate heater required.</p>
        ) : (
          <Group title="Recommended Heaters">
            <Tile role="Best match heater" p={c.bestHeater} badge="Best Match" />
            <Tile role="Premium upgrade" p={c.premiumHeater} badge="Premium" />
            <Tile role="Commercial option" p={c.commercialHeater} />
            {otherHeaters.slice(0, 1).map((h) => <Tile key={h.id} role="Compatible heater" p={h} />)}
          </Group>
        )}
        {!c.infrared && (
          <Group title="Heater Essentials">
            <Tile role="Required rocks" p={c.rocks} />
            <Tile role="Digital controls" p={c.controls} />
            <Tile role="Heater package" p={c.heaterPackage} />
          </Group>
        )}
        <Group title="Comfort & Ambience">
          <Tile role="Essential oils" p={c.oils} />
          <Tile role="Salt wall" p={c.saltWall} />
          <Tile role="Lighting" p={c.lighting} />
          <Tile role="Backrests" p={c.backrests} />
        </Group>
        <Group title="Complete the Ritual">
          <Tile role="Cold plunge pairing" p={c.coldPlunge} />
          <Tile role="Hot tub pairing" p={c.hotTub} />
        </Group>
        <p className="text-cream/40 text-xs mt-6 italic">
          Recommended based on sauna type and size. Confirm final compatibility with The Recovery Shops before installation.
        </p>
      </div>
    );
  }

  if (companions.type === "heater") {
    const c = companions;
    return (
      <div className="mt-16" data-testid="heater-companions">
        <p className="overline flex items-center gap-2"><Sparkles size={13} /> Pairs Perfectly With</p>
        <h2 className="font-serif text-2xl sm:text-3xl text-cream mt-2">Compatible saunas & essentials</h2>
        <p className="text-cream/55 text-sm mt-2">
          Ideal for {c.compatibleSaunaTypes?.join(", ")} saunas{c.kind === "wood" ? " (wood-burning)" : ""}.
        </p>
        <Group title="Compatible Saunas">
          {(c.compatibleSaunas || []).map((s) => <Tile key={s.id} role={`${s.subtype || "Sauna"}`} p={s} />)}
        </Group>
        <Group title="Heater Essentials">
          <Tile role="Required rocks" p={c.rocks} />
          <Tile role="Digital controls" p={c.controls} />
          <Tile role="Heater guard" p={c.heaterGuard} />
          <Tile role="Heater package" p={c.heaterPackage} />
        </Group>
        <Group title="Comfort & Ambience">
          <Tile role="Essential oils" p={c.oils} />
          <Tile role="Salt wall" p={c.saltWall} />
          <Tile role="Lighting" p={c.lighting} />
        </Group>
      </div>
    );
  }
  return null;
}
